'use client';

// `useAppRouter()` — drop-in replacement for `useRouter()` from
// `next/navigation`. Same shape, same method names. The only
// difference is that `push`, `replace`, and `prefetch` automatically
// run their `href` argument through `useNetworkAwarePath()`, so any
// imperative navigation respects the user's network mode.
//
// Why this exists: it lets engineers write `router.push('/markets')`
// without thinking about demo-mode prefixing — the prefix is added
// at the wrapper layer. That migration cost is one line per file
// (import swap), no behavioral changes elsewhere.
//
// Direct `useRouter` from `next/navigation` is banned by precommit
// FQ-127 (with a small allowlist for files that legitimately own URL
// flips, like the wallet toggle in `lib/network/switcher.ts`).
import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { useNetworkAwarePath } from './use-network-aware-path';

/** Mirror of Next.js's `AppRouterInstance` shape. Re-derived from the
 *  return type of `useRouter()` so we stay in lockstep with whatever
 *  Next.js promises — without depending on a non-exported type. */
export type AppRouter = ReturnType<typeof useRouter>;
export function useAppRouter() {
  const $ = _c(17);
  const router = useRouter();
  const networkPath = useNetworkAwarePath();
  let t0;
  let t1;
  let t2;
  if ($[0] !== networkPath || $[1] !== router) {
    t0 = (href, options) => {
      router.push(networkPath(href), options);
    };
    t1 = (href_0, options_0) => {
      router.replace(networkPath(href_0), options_0);
    };
    t2 = (href_1, options_1) => {
      router.prefetch(networkPath(href_1), options_1);
    };
    $[0] = networkPath;
    $[1] = router;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t0 = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  let t4;
  let t5;
  if ($[5] !== router) {
    t3 = () => router.back();
    t4 = () => router.forward();
    t5 = () => router.refresh();
    $[5] = router;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== router.bfcacheId || $[10] !== t0 || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      bfcacheId: router.bfcacheId,
      push: t0,
      replace: t1,
      prefetch: t2,
      back: t3,
      forward: t4,
      refresh: t5
    };
    $[9] = router.bfcacheId;
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}